import { useQuery, gql } from '@apollo/client';
import useLoginState from '../../core/hooks/useLoginState';
import useUserBranches from '../../core/queries/useUserBranches';
import type { FutureStockQuantities, StockQuantity } from '../types';

export interface UseProductNodeStockAtLocationOptions {
    skip?: boolean;
    variantSkus?: string[];
}

export const productNodeStockQuantityQuery = gql`
    query productNodeStockQuantity(
        $store: ID!
        $skuList: [String!]!
        $location: String!
    ) {
        stockQuantities(store: $store, skuList: $skuList, location: $location) {
            store
            sku
            location
            quantity
            nextAvailability
        }
        productListFutureStock(
            store: $store
            skuList: $skuList
            location: $location
        ) {
            store
            sku
            location
            quantities {
                quantity
                date
            }
        }
    }
`;

const useProductStockQuantityQuery = ({
    store,
    skuList,
    stockLocation,
    options,
}: {
    store: string;
    skuList: string[];
    stockLocation?: string;
    options?: UseProductNodeStockAtLocationOptions;
}) => {
    const isLoggedIn = useLoginState();
    const { mainStock, locationCode } = useUserBranches();

    let location = stockLocation;

    if (!location) {
        location = mainStock?.id ?? locationCode;
    }

    const { data, error, ...rest } = useQuery<{
        stockQuantities: StockQuantity[];
        productListFutureStock: FutureStockQuantities[];
    }>(productNodeStockQuantityQuery, {
        variables: {
            store,
            skuList,
            location,
        },
        skip: !location || skuList.length === 0 || !isLoggedIn || options?.skip,
        fetchPolicy: 'cache-first',
    });

    return {
        data,
        error,
        location,
        ...rest,
    };
};

const useProductNodeStockQuantity = ({
    store,
    skuList,
    stockLocation,
    options,
}: {
    store: string;
    skuList: string[];
    stockLocation?: string;
    options?: UseProductNodeStockAtLocationOptions;
}) => {
    const { data, location, error, ...rest } = useProductStockQuantityQuery({
        store,
        skuList,
        stockLocation,
        options,
    });

    return { data, ...rest, loading: rest.loading, error };
};

export default useProductNodeStockQuantity;
